import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

// Generic hook for API data fetching
export function useApiData<T>(
  fetchFunction: () => Promise<T>,
  dependencies: any[] = []
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchFunction();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('API fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, dependencies);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
}

// Hook for products
export function useProducts() {
  return useApiData(
    () => apiService.getProducts(),
    []
  );
}

// Hook for a single product
export function useProduct(id: string) {
  return useApiData(
    () => apiService.getProduct(id),
    [id]
  );
}

// Hook for products by category
export function useProductsByCategory(category: string) {
  return useApiData(
    () => apiService.getProductsByCategory(category),
    [category]
  );
}

// Hook for tutorials with pagination
export function useTutorials(page: number = 1, limit: number = 10, language: string = 'en') {
  return useApiData(
    () => apiService.getTutorials(page, limit, language),
    [page, limit, language]
  );
}

// Hook for all tutorials (for backward compatibility - use sparingly)
export function useAllTutorials() {
  return useApiData(
    () => apiService.getTutorials(1, 1000), // Large limit to get all
    []
  );
}

// Hook for a single tutorial
export function useTutorial(id: string) {
  return useApiData(
    () => apiService.getTutorial(id),
    [id]
  );
}

// Hook for tutorials by category
export function useTutorialsByCategory(category: string) {
  return useApiData(
    () => apiService.getTutorialsByCategory(category),
    [category]
  );
}

// Hook for search functionality
export function useSearch(query: string, type?: 'product' | 'tutorial' | 'user') {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async () => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // For now, we'll implement a simple client-side search
      // In a real app, you'd have a dedicated search endpoint
      const [productsResponse, tutorialsResponse] = await Promise.all([
        apiService.getProducts(),
        apiService.getTutorials(1, 100, 'en') // Limit to 100 tutorials for search, use English for now
      ]);

      const allResults = [
        ...(productsResponse.products || []).map(product => ({
          ...product,
          type: 'product' as const,
          searchableText: `${product.name} ${product.description} ${product.category}`.toLowerCase()
        })),
        ...(tutorialsResponse.tutorials || []).map(tutorial => ({
          ...tutorial,
          type: 'tutorial' as const,
          searchableText: `${tutorial.title} ${tutorial.description} ${tutorial.category}`.toLowerCase()
        }))
      ];

      const filteredResults = allResults.filter(item => {
        if (type && item.type !== type) return false;
        return item.searchableText.includes(query.toLowerCase());
      });

      setResults(filteredResults);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  }, [query, type]);

  useEffect(() => {
    const timeoutId = setTimeout(search, 300); // Debounce search
    return () => clearTimeout(timeoutId);
  }, [search]);

  return { results, loading, error };
}
